import { useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { publicApi, usePublic } from "@/lib/api";

// Minimal markdown: headings, lists, tables, paragraphs (legal text is our own, rendered as text, never HTML).
export function Markdown({ text = "" }) {
  const blocks = text.split(/\n\s*\n/);
  return (
    <div className="space-y-4 text-[15px] leading-relaxed text-slate-700">
      {blocks.map((b, i) => {
        const lines = b.trim().split("\n");
        if (b.startsWith("## ")) return <h2 key={i} className="pt-2 font-display text-xl font-bold text-slate-900">{b.slice(3)}</h2>;
        if (lines[0].startsWith("|")) {
          const rows = lines.filter((l) => !/^\|\s*-/.test(l)).map((l) => l.split("|").slice(1, -1).map((c) => c.trim()));
          return <div key={i} className="overflow-x-auto"><table className="w-full text-sm"><thead><tr>{rows[0].map((c) => <th key={c} className="border-b p-2 text-left">{c}</th>)}</tr></thead><tbody>{rows.slice(1).map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k} className="border-b border-slate-100 p-2">{c}</td>)}</tr>)}</tbody></table></div>;
        }
        if (/^(- |\d+\. )/.test(lines[0])) return <ul key={i} className="list-disc space-y-1 pl-5">{lines.map((l) => <li key={l}>{l.replace(/^(- |\d+\. )/, "")}</li>)}</ul>;
        return <p key={i}>{b}</p>;
      })}
    </div>
  );
}

export function LegalPage() {
  const { slug } = useParams();
  const v = new URLSearchParams(window.location.search).get("version");
  const d = usePublic(`/legal/${slug}${v ? `?version=${v}` : ""}`);
  if (!d) return <p className="p-10 text-slate-400">Loading…</p>;
  return (
    <article className="mx-auto max-w-3xl px-5 py-16" data-testid="legal-page">
      <h1 className="font-display text-4xl font-extrabold">{d.title}</h1>
      <p className="mt-2 text-sm text-slate-500" data-testid="legal-version">Version {d.version} · Effective {d.effective}</p>
      <div className="mt-8"><Markdown text={d.body} /></div>
    </article>
  );
}

export function LegalArchive() {
  const cfg = useOutletContext();
  return (
    <section className="mx-auto max-w-3xl px-5 py-16" data-testid="legal-archive">
      <h1 className="font-display text-4xl font-extrabold">Legal documents: all versions</h1>
      <ul className="mt-8 divide-y rounded-3xl border border-slate-200 bg-white">
        {(cfg?.legal || []).map((d) => (
          <li key={d.slug} className="flex flex-wrap items-center justify-between gap-2 px-6 py-4"><span className="font-semibold">{d.title}</span>
            <span className="flex gap-2">{d.versions.map((v) => <Link key={v} to={`/legal/${d.slug}?version=${v}`} className="rounded-full border px-3 py-1 text-xs hover:border-slate-900">v{v}</Link>)}</span></li>
        ))}
      </ul>
    </section>
  );
}

export function Resources() {
  const cfg = useOutletContext();
  return (
    <section className="mx-auto max-w-4xl px-5 py-16">
      <h1 className="font-display text-4xl font-extrabold sm:text-5xl">Guides</h1>
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {(cfg?.content?.resources || []).map((r) => <Link key={r.slug} to={`/resources/${r.slug}`} className="rounded-3xl border border-slate-200 bg-white p-6 hover:border-slate-900" data-testid={`resource-${r.slug}`}><p className="font-display text-xl font-bold">{r.title}</p><p className="mt-2 line-clamp-2 text-sm text-slate-600">{r.body}</p></Link>)}
      </div>
    </section>
  );
}

export function Resource() {
  const { slug } = useParams();
  const cfg = useOutletContext();
  const r = (cfg?.content?.resources || []).find((x) => x.slug === slug);
  if (!r) return <p className="p-10 text-slate-400">{cfg ? "Guide not found." : "Loading…"}</p>;
  return <article className="mx-auto max-w-3xl px-5 py-16"><h1 className="font-display text-4xl font-extrabold">{r.title}</h1><div className="mt-8"><Markdown text={r.body} /></div><Link to="/signup" className="mt-10 inline-block rounded-full bg-slate-900 px-6 py-3 font-semibold text-white">Let InvoiceReconciler.app do this for you</Link></article>;
}

export function Contact() {
  const cfg = useOutletContext();
  const [f, setF] = useState({ name: "", email: "", pharmacy: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await publicApi("post", "/contact", f); setSent(true); } catch (err) { toast.error(err.message); }
  };
  const co = cfg?.company || {};
  return (
    <section className="mx-auto grid max-w-5xl gap-10 px-5 py-16 md:grid-cols-2">
      <div>
        <h1 className="font-display text-4xl font-extrabold sm:text-5xl">Talk to us</h1>
        <p className="mt-4 text-slate-600">Questions about the service, pricing or getting set up. Support is by email, weekdays 9 to 5.</p>
        {co.email && <p className="mt-6">Email: <a className="font-semibold text-teal-700" href={`mailto:${co.email}`} data-testid="contact-email-link">{co.email}</a></p>}
        {co.booking_url && <a href={co.booking_url} target="_blank" rel="noreferrer" className="mt-4 inline-block rounded-full border border-slate-900 px-5 py-2.5 font-semibold" data-testid="contact-booking-link">Book a 15-minute walkthrough</a>}
      </div>
      {sent ? <p className="rounded-3xl border border-teal-200 bg-teal-50 p-8 font-semibold text-teal-900" data-testid="contact-sent">Thanks — we'll reply by email.</p> : (
        <form onSubmit={submit} className="space-y-3 rounded-3xl border border-slate-200 bg-white p-6" data-testid="contact-form">
          <Input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" />
          <Input required type="email" placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" />
          <Input placeholder="Pharmacy (optional)" value={f.pharmacy} onChange={(e) => setF({ ...f, pharmacy: e.target.value })} data-testid="contact-pharmacy-input" />
          <Textarea required rows={5} placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" />
          <Button type="submit" className="w-full" data-testid="contact-submit-button">Send message</Button>
        </form>
      )}
    </section>
  );
}
